import React from "react";
import { Avatar, Box, IconButton, MenuItem, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesBufferChannel } from "../../../Models/voicesProfiles";
import {
  useRefreshVoicesBufferChannels,
  useVoicesBufferChannels,
} from "../../../Query/useVoicesProfiles";
import VoicesInput from "../Common/VoicesInput";

interface Props {
  value: string | null;
  channelType: "profile" | "page";
  onChange: (channel: VoicesBufferChannel | null) => void;
}

const NONE = "__none__";

const VoicesChannelSelect: React.FC<Props> = ({ value, channelType, onChange }) => {
  const { t } = useTranslation("voicesProfiles");
  const { data, isLoading, isError } = useVoicesBufferChannels();
  const connected = data?.connected ?? false;
  const channels = data?.channels ?? [];
  const refresh = useRefreshVoicesBufferChannels();

  const options = channels.filter((channel) => channel.type === channelType);
  const isUnknown = !!value && !options.some((channel) => channel.id === value);

  const usedBy = (channel: VoicesBufferChannel) => {
    if (!channel.assigned || channel.id === value) return null;
    return channel.assigned.name
      ? t("channelUsedBy", { name: channel.assigned.name })
      : t("channelUsedElsewhere");
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <VoicesInput
        select
        disabled={isLoading || (!!data && !connected)}
        value={value ?? NONE}
        onChange={(e) =>
          onChange(
            options.find((channel) => channel.id === e.target.value) ?? null,
          )
        }
        error={isError || isUnknown}
        helperText={
          isError
            ? t("channelsUnavailable")
            : data && !connected
              ? t("bufferNotConnectedForWorkspace")
              : isUnknown
              ? t("channelNotInBuffer")
              : !options.length && !isLoading
                ? t(channelType === "page" ? "noPageChannels" : "noProfileChannels")
                : undefined
        }
      >
        <MenuItem value={NONE}>{t("noChannel")}</MenuItem>
        {isUnknown && <MenuItem value={value as string}>{value}</MenuItem>}
        {options.map((channel) => (
          <MenuItem
            key={channel.id}
            value={channel.id}
            disabled={!channel.is_usable || !!usedBy(channel)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={channel.avatar ?? undefined}
                sx={{ width: 24, height: 24 }}
              />
              <Box>
                <Typography variant="body2">
                  {channel.display_name ?? channel.name}
                </Typography>
                {(usedBy(channel) || !channel.is_usable) && (
                  <Typography variant="caption" sx={{ color: color.gray500 }}>
                    {usedBy(channel) ?? t("channelNotUsable")}
                  </Typography>
                )}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </VoicesInput>
      {value && (
        <Tooltip title={t("disconnectChannel")}>
          <IconButton
            size="small"
            onClick={() => onChange(null)}
            sx={{ mt: 0.5, color: color.gray500 }}
          >
            <LinkOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t("refreshChannels")}>
        <span>
          <IconButton
            size="small"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            sx={{ mt: 0.5, color: color.gray500 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default VoicesChannelSelect;
